import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { apiErrorMessage } from "../lib/errors";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    starting_bankroll: user?.starting_bankroll || 1000,
    base_stake: user?.base_stake || 10,
    table_limit: user?.table_limit || 2000,
    digest_enabled: user?.digest_enabled || false,
    digest_hour_utc: user?.digest_hour_utc ?? 13,
    weekly_recap_enabled: user?.weekly_recap_enabled ?? true,
    leaderboard_opt_in: user?.leaderboard_opt_in ?? false,
  });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile({
        name: form.name || null,
        starting_bankroll: Number(form.starting_bankroll),
        base_stake: Number(form.base_stake),
        table_limit: Number(form.table_limit),
        digest_enabled: Boolean(form.digest_enabled),
        digest_hour_utc: Number(form.digest_hour_utc),
        weekly_recap_enabled: Boolean(form.weekly_recap_enabled),
        leaderboard_opt_in: Boolean(form.leaderboard_opt_in),
      });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Update failed"));
    } finally {
      setLoading(false);
    }
  };

  const sendTestDigest = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/send-now");
      toast.success(`Digest sent to ${data.to}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Send failed — is RESEND_API_KEY set?"));
    } finally {
      setSending(false);
    }
  };

  const hourLabel = (h) => {
    const d = new Date();
    d.setUTCHours(h, 0, 0, 0);
    return d.toLocaleTimeString(undefined, { hour: "numeric", timeZoneName: "short" });
  };

  return (
    <div data-testid="profile-page">
      <div className="border-b border-zinc-800 pb-10 mb-10">
        <div className="overline mb-3">OPERATOR</div>
        <h1 className="font-display text-4xl lg:text-5xl">Your bankroll settings.</h1>
        <p className="mt-4 text-zinc-400 max-w-2xl">
          Set the numbers that seed your calculator, daily plan and morning email.
          Nothing leaves your account.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <form
          onSubmit={submit}
          className="lg:col-span-6 border border-zinc-800 p-6 bg-zinc-950/60 space-y-5"
        >
          <div>
            <label className="overline block mb-2">EMAIL</label>
            <input
              readOnly
              value={user?.email || ""}
              data-testid="profile-email"
              className="w-full bg-zinc-900/80 border border-zinc-800 px-3 py-2 mono text-sm text-zinc-400 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="overline block mb-2">HANDLE</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="profile-name"
              className="w-full bg-transparent border border-zinc-800 px-3 py-2 mono text-sm focus:outline-none focus:border-[#00D4FF]"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="overline block mb-2">BANKROLL</label>
              <input
                type="number"
                min={10}
                value={form.starting_bankroll}
                onChange={(e) => setForm({ ...form, starting_bankroll: e.target.value })}
                data-testid="profile-bankroll"
                className="w-full bg-transparent border border-zinc-800 px-2 py-2 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              />
            </div>
            <div>
              <label className="overline block mb-2">BASE STAKE</label>
              <input
                type="number"
                min={1}
                value={form.base_stake}
                onChange={(e) => setForm({ ...form, base_stake: e.target.value })}
                data-testid="profile-base-stake"
                className="w-full bg-transparent border border-zinc-800 px-2 py-2 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              />
            </div>
            <div>
              <label className="overline block mb-2">TABLE LIMIT</label>
              <input
                type="number"
                min={0}
                value={form.table_limit}
                onChange={(e) => setForm({ ...form, table_limit: e.target.value })}
                data-testid="profile-table-limit"
                className="w-full bg-transparent border border-zinc-800 px-2 py-2 mono text-sm focus:outline-none focus:border-[#00D4FF]"
              />
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <div className="overline neon-text mb-3 flex items-center gap-2">
              <Mail size={12} strokeWidth={1.5} /> MORNING EMAIL DIGEST
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.digest_enabled}
                onChange={(e) => setForm({ ...form, digest_enabled: e.target.checked })}
                data-testid="profile-digest-enabled"
                className="w-4 h-4 accent-[#00D4FF]"
              />
              <span className="text-sm text-zinc-300">
                Send me a daily card at{" "}
                <span className="mono neon-text">
                  {hourLabel(Number(form.digest_hour_utc))}
                </span>
              </span>
            </label>

            <div className="mt-4">
              <label className="overline block mb-2">
                SEND HOUR (UTC) · {form.digest_hour_utc}:00
              </label>
              <input
                type="range"
                min={0}
                max={23}
                step={1}
                value={form.digest_hour_utc}
                onChange={(e) => setForm({ ...form, digest_hour_utc: e.target.value })}
                data-testid="profile-digest-hour"
                className="w-full accent-[#00D4FF]"
              />
            </div>

            <label className="mt-5 flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.weekly_recap_enabled}
                onChange={(e) =>
                  setForm({ ...form, weekly_recap_enabled: e.target.checked })
                }
                data-testid="profile-weekly-recap"
                className="w-4 h-4 accent-[#00D4FF]"
              />
              <span className="text-sm text-zinc-300">
                Send me a weekly recap every Monday (~9am ET) with my bankroll stats
              </span>
            </label>


            <button
              type="button"
              onClick={sendTestDigest}
              disabled={sending}
              data-testid="profile-digest-test"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-zinc-800 text-zinc-200 hover:border-[#00D4FF] hover:text-[#00D4FF] font-mono uppercase tracking-widest text-xs disabled:opacity-50"
            >
              <Send size={12} strokeWidth={1.5} />{" "}
              {sending ? "Sending…" : "Send Test Digest Now"}
            </button>
          </div>

          <div className="border-t border-zinc-800 pt-5">
            <div className="overline neon-text mb-3">PUBLIC LEADERBOARD</div>
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.leaderboard_opt_in}
                onChange={(e) =>
                  setForm({ ...form, leaderboard_opt_in: e.target.checked })
                }
                data-testid="profile-leaderboard-optin"
                className="w-4 h-4 mt-0.5 accent-[#00D4FF]"
              />
              <span className="text-sm text-zinc-300 leading-relaxed">
                Show my handle <span className="mono neon-text">{form.name || "—"}</span>{" "}
                on the{" "}
                <a href="/leaderboard" target="_blank" rel="noreferrer" className="underline text-zinc-200">
                  public leaderboard
                </a>
                . Only handle + % gain + W/L are shared — never your email or exact
                bankroll.
              </span>
            </label>
          </div>


          <button
            type="submit"
            disabled={loading}
            data-testid="profile-submit"
            className="px-6 py-3 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs disabled:opacity-50 hover:brightness-110"
          >
            {loading ? "Saving…" : "Save settings"}
          </button>
        </form>

        <aside className="lg:col-span-6 space-y-4">
          <div className="border border-zinc-800 p-6">
            <div className="overline neon-text mb-3">RULES OF THE HOUSE</div>
            <ul className="space-y-3 text-sm text-zinc-400 leading-relaxed">
              <li>
                <strong className="text-white">Bankroll</strong> is the TOTAL money you've
                allocated for this entire endeavor — not a deposit. Never increase it to
                chase.
              </li>
              <li>
                <strong className="text-white">Base stake</strong> should be ≤ 2% of your
                bankroll for Classic Martingale to survive typical variance.
              </li>
              <li>
                <strong className="text-white">Table limit</strong> is the maximum single
                wager your book allows. At many US books this is $2,500–$10,000 depending
                on market.
              </li>
            </ul>
          </div>
          <div className="border border-amber-500/40 bg-amber-500/5 p-6">
            <div className="overline amber-text mb-2">⚠ REMEMBER</div>
            <p className="text-sm text-amber-200 leading-relaxed">
              Martingale is a variance-shifting strategy, not an edge. A negative-EV bet
              progressed Martingale-style is still negative-EV over time. Only use this on
              plays you'd take at normal stakes anyway.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}